import { IPeriodByMonth } from "@edp-models/energy-comparison.model";
import { IAppState } from "@edp-store/state/app.state";
import { IDefineHouseState } from "@edp-store/state/define-house.state";
import { createSelector } from "@ngrx/store";

const defineHouseSelector = (state: IAppState) => state.defineHouse;

export const getDefineHouseAnswers = createSelector(defineHouseSelector, (state: any) => state.serviceAnswers);

export const getCurrentData = createSelector(defineHouseSelector, (state: any, step: number) => {
    const stepString = step.toString();

    if (!!state.serviceAnswers) {
        const stepObj = state.serviceAnswers[0].house.filter((data: any) => data.category === stepString);

        const transformArrayToObject = (arr: any[]) => {
            return arr.reduce((acc, obj) => {
                acc[obj.queryCode] = obj.data;
                return acc;
            }, {} as { [key: string]: string });
        };

        const result = transformArrayToObject(stepObj);

        return result;
    }
});

export const getActiveStepIndex = createSelector(defineHouseSelector, (state: IDefineHouseState) => state.activeStepIndex);

export const getStepsWithActive = createSelector(defineHouseSelector, (state: IDefineHouseState) => {
    if (!state.energySourceSubsteps) {
        return [];
    }

    const correctOrder = ["gas", "bottle-gas", "thermal-solar", "photovoltaic-solar", "diesel", "pellets", "wood"];
    const sortedSteps = correctOrder.filter(step => state.energySourceSubsteps.includes(step));

    return (
        sortedSteps?.map((step, index) => {
            const isActive = index <= state.activeStepIndex;
            const isNext = index === state.activeStepIndex + 1;
            const isPrevious = index === state.activeStepIndex - 1;
            const isFirstStep = index === 0;
            const isLastStep = index === sortedSteps.length - 1;

            return {
                id: step,
                active: isActive,
                next: isNext,
                previous: isPrevious,
                firstStep: isFirstStep,
                lastStep: isLastStep,
            };
        }) || []
    );
});

export const getEnergySourceSubsteps = createSelector(defineHouseSelector, (state: any, props: { step: string; page: string }) => {
    if (!!state.serviceAnswers) {
        const stepAnswers = state.serviceAnswers[0].house.filter((data: any) => data.category === props.step && data.page === props.page);

        const transformArrayToObject = (arr: any[]) => {
            return arr.reduce((acc, obj) => {
                acc[obj.queryCode] = obj.data;
                return acc;
            }, {} as { [key: string]: string });
        };

        const result = transformArrayToObject(stepAnswers);
        return result;
    }
});

export const getAnswers = createSelector(defineHouseSelector, (state: IDefineHouseState, props: { step: string; page: string }) => {
    if (!!state.answers) {
        const answers = state.answers
            .filter((data: any) => data.category === props.step && data.page === props.page)
            .reduce((acc, { category, page, ...answer }) => ({ ...acc, ...answer }), {});

        return transformStringToArray(answers);
    }
    return {};
});

export const getDefineHouseTermsConditions = createSelector(defineHouseSelector, (state: IDefineHouseState) => {
    if (!!state.serviceAnswers) {
        const termsAndConditions = state.serviceAnswers[0].contract.termsAndConditions;

        return termsAndConditions;
    }
    return null;
});

export const getActiveEquipmentStepIndex = createSelector(
    defineHouseSelector,
    (state: IDefineHouseState) => state.activeEquipmentStepIndex
);

export const getEquipmentStepsWithActive = createSelector(defineHouseSelector, (state: IDefineHouseState) => {
    const correctOrder = ["kitchen", "air-conditioning", "water-heating", "washing", "entertainment", "other"];

    return (
        correctOrder?.map((step, index) => {
            const isActive = index <= state.activeEquipmentStepIndex;
            const isNext = index === state.activeEquipmentStepIndex + 1;
            const isPrevious = index === state.activeEquipmentStepIndex - 1;
            const isFirstStep = index === 0;
             const isLastStep = index === correctOrder.length - 1;

            return {
                id: step,
                active: isActive,
                next: isNext,
                previous: isPrevious,
                firstStep: isFirstStep,
                lastStep: isLastStep,
            };
        }) || []
    );
});

export const getActiveThermalInsulationStepIndex = createSelector(
    defineHouseSelector,
    (state: IDefineHouseState) => state.activeThermalInsulationStepIndex
);

export const getThermalInsulationStepsWithActive = createSelector(defineHouseSelector, (state: IDefineHouseState) => {
    const correctOrder = ["wall-thermally",  "roof-thermally", "sunset-facade", "outdoor-facade", "type-glass"];

    return (
        correctOrder?.map((step, index) => {
            const isActive = index <= state.activeThermalInsulationStepIndex;
            const isNext = index === state.activeThermalInsulationStepIndex + 1;
            const isPrevious = index === state.activeThermalInsulationStepIndex - 1;
            const isFirstStep = index === 0;
             const isLastStep = index === correctOrder.length - 1;

            return {
                id: step,
                active: isActive,
                next: isNext,
                previous: isPrevious,
                firstStep: isFirstStep,
                lastStep: isLastStep,
            };
        }) || []
    );
});

const transformStringToArray = obj => {
    for (const key in obj) {
        if (typeof obj[key] === "string" && obj[key].startsWith("[") && obj[key].endsWith("]")) {
            try {
                const parsed = JSON.parse(obj[key]);
                if (Array.isArray(parsed)) {
                    obj[key] = parsed;
                }
            } catch (e) {}
        }
    }
    return obj;
};

export const getHouseStatusMoonshot = createSelector(defineHouseSelector, (state: IDefineHouseState) => state.statusMoonShot);

export const getEnergyComparisonPeriod = (period: IPeriodByMonth) =>
    createSelector(defineHouseSelector, (state: IDefineHouseState) => {
        const energyComparisonPeriods = state.energyComparison?.periods;

        if (!energyComparisonPeriods) return null;

        return energyComparisonPeriods[period];
    });

export const getEnergyComparisonProgressBar = (period: IPeriodByMonth) =>
    createSelector(defineHouseSelector, (state: IDefineHouseState) => {

        const energyComparisonPeriods = state.energyComparison?.periods;

        if (!energyComparisonPeriods) return null;

        const periodData = energyComparisonPeriods[period];
    
        const maxConsumption = Math.max(...Object.values(periodData).map(item => item.consumption));

        const energyComparasion = Object.keys(periodData).reduce((acc, key) => {
            const consumption = periodData[key].consumption;

            let percentual = (consumption / maxConsumption) * 100;
          
            if (percentual < 10) {
              percentual = 10;
            }

            acc[key] = {
                progressBar: parseFloat(percentual.toFixed(2)),
                consumption
            }
          
            return acc;
          }, {} as Record<string, { progressBar: number, consumption: number }>);
    
        return energyComparasion;
    }); 

export const getHouseEnergyComparison = createSelector(defineHouseSelector, (state: IDefineHouseState) => state.energyComparison);